import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import {
  Table,
  TableBody,
  TableHead,
  TableContainer,
  TableRow,
  Paper,
  Container,
  Typography,
  Link,
  Tooltip,
  Stack,
  TextField,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  OutlinedInput
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import AddLogo from '../components/StyleGuide/AddLogo';
import AddColor from '../components/StyleGuide/AddColor';
import AddFont from '../components/StyleGuide/AddFont';
import { getOrganization } from '../apis/apiList/organizationApi';
import { deleteStyleDesign, getStyleDesignList, updateStyleGuideNotes } from '../apis/apiList/styleDesignApi';
import { useApp } from '../context/AppContext';
import BeatLoader from "react-spinners/BeatLoader";
import theme from "../theme/theme";
import { useMessage } from '../context/MessageContext';
import DeleteConfirmationModal from '../components/StyleGuide/DeleteConfirmationModal';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#495967',
      color: theme.palette.common.white,
      fontWeight: 600
  },
  [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: 'rgba(0,0,0,.09)',
  '&:nth-of-type(odd)': {
      // backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
      border: 0,
  },
}));


function StyleGuide() {

  const { id } = useParams();
  const { userDetails } = useApp();
  const { showMessage } = useMessage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFontModalOpen, setIsFontModalOpen] = useState(false);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [organizationInfo, setOrganizationInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [logoList, setLogoList] = useState([]);
  const [fontList, setFontList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [logoNote, setLogoNote] = useState('');
  const [colorNote, setColorNote] = useState('');
  const [fontNote, setFontNote] = useState('');
  const [open, setOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteStyleId, setDeleteStyleId] = useState('');
  const [deleteStyleType, setDeleteStyleType] = useState('');

  useEffect(()=>{

    async function fetchData() {
      setIsLoading(true);
      let headers = {
        headers: { 'x-access-token': userDetails.jwtToken },
      }
      
      try {
        // Fetch organization details
        const organization = await getOrganization({ id }, headers);
        console.log('Organization on style guide page:', organization);
        setOrganizationInfo(organization.data);
    
        // Fetch style design list
        try {
          const styleData = await getStyleDesignList(id, headers);
          console.log('Style design list data:', styleData);
    
          setColorList(styleData.data?.colors.length > 0 ? styleData.data.colors : []);
          setFontList(styleData.data?.fonts.length > 0 ? styleData.data.fonts : []);
          setLogoList(styleData.data?.logos.length > 0 ? styleData.data.logos : []);
          setLogoNote(styleData.data?.logoNotes || '');
          setColorNote(styleData.data?.colorNotes || '');
          setFontNote(styleData.data?.fontNotes || '');
        } catch (styleError) {
          console.error('Error fetching style design list:', styleError);
          showMessage(styleError.response?.data.message || 'Failed to fetch style design list. Please try again later.', 'error');
        }
      } catch (orgError) {
        console.error('Error fetching organization details:', orgError);
        showMessage('Something went wrong. Please try again later.', 'error');
      } finally {
        setIsLoading(false);
      }
    }
    
    if(id){
      // getOrganizationDetails();
      // fetchStyleDesignList();
      fetchData();
    }
  }, [id])


  const updateList = (response, type, link) => {
    if (!response || !type) return;
    let existingIds = null;
    let onlyNewObj = null;
    switch (type) {
      case "logos":
        existingIds = new Set(logoList.map((f) => f._id));
        onlyNewObj = response.filter((f) => !existingIds.has(f._id));
        if (onlyNewObj.length > 0) {
          setLogoList((prev) => [...prev, {...onlyNewObj[0], fileUrl: link}]);
        }
        break;
  
      case "fonts":
        existingIds = new Set(fontList.map((f) => f._id));
        onlyNewObj = response.filter((f) => !existingIds.has(f._id));
        if (onlyNewObj.length > 0) {
          setFontList((prev) => [...prev, {...onlyNewObj[0], fileUrl: link}]);
        }
        break;
  
      case "colors":
        existingIds = new Set(colorList.map((f) => f._id));
        onlyNewObj = response.filter((f) => !existingIds.has(f._id));
        if (onlyNewObj.length > 0) {
          setColorList((prev) => [...prev, ...onlyNewObj]);
        }
        break;
  
      default:
        console.error("Invalid type provided for updateList");
    }
  };
  

  const handleFileChange = (e) => {
    // const file = e.target.files[0];
    // if (file) setFileName(file.name);
  };

  const handleLogo = () => {
    setIsModalOpen(true);
  }
  const handleColor = () => {
    setIsColorModalOpen(true);
  }
  const handleFont = () => {
    setIsFontModalOpen(true);
  }

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName || 'download'; // Default name if fileName is not provided
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleNoteSubmit = async (notes) =>{
    setIsCreatingNote(true);
    let data ={
      logoNotes: logoNote,
      colorNotes: colorNote,
      fontNotes: fontNote,
      organizationId: organizationInfo._id,
    }
    try {
      const response = await updateStyleGuideNotes(data, {
        headers: {
          "x-access-token": userDetails?.jwtToken
        },
      });
      console.log('response', response)
      if (response.success) {
        showMessage(response.message, "success");
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      // Handle error (e.g., show a message to the user)
      showMessage(err?.response?.data?.message || 'Internal server issue', "error");
    } finally {
      setIsCreatingNote(false);
    }
  }

  const handleDelete = async () => {
    setDeleteLoading(true);
    if (!deleteStyleId || !deleteStyleType) return;
    let data = {
      organizationId: organizationInfo._id,
      designId: deleteStyleId,
      designType: deleteStyleType,
    }
    try {
      const response = await deleteStyleDesign(data, {
        headers: {
          "x-access-token": userDetails?.jwtToken,
        },
      });
      if (response.success) {
        showMessage("Item deleted successfully", "success");
  
        switch (deleteStyleType) {
          case "logos":
            setLogoList((prevLogos) => prevLogos.filter((logo) => logo._id !== deleteStyleId));
            break;
          case "fonts":
            setFontList((prevFonts) => prevFonts.filter((font) => font._id !== deleteStyleId));
            break;
          case "colors":
            setColorList((prevColors) => prevColors.filter((color) => color._id !== deleteStyleId));
            break;
          default:
            console.error("Invalid type provided for deletion");
        }
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      showMessage(error.response?.data?.message || "Failed to delete item", "error");
    } finally {
      setDeleteLoading(false);
      setOpen(false);
    }
  };

  const handleDeleteType = (type, id) => {
    setDeleteStyleId(id);
    setDeleteStyleType(type);
    setOpen(true);
  }

  const override = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: "50%",
    left: "50%",
  };
  
  return (
    <>
      {isLoading ? (
        <Box sx={override}>
          <BeatLoader
            color={theme.palette.primary.main}
            loading={isLoading}
            size={25}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </Box>
      )
      : (
        <Box>
          <Box>
            <img
              src="/images/static-icon/flat-lay-desk-with-coffee-keyboard.jpg"
              style={{
                width: "100%",
                height: "300px",
                objectFit: "cover",
                objectPosition: "0 81%",
                marginBottom: "30px",
              }}
            />
            <h4 className="styleGuideTitle">Style Guide</h4>
          </Box>
          <Box sx={{ textAlign: "end", marginBottom: "35px" }}>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              {/* <InputLabel id="demo-select-small-label">Age</InputLabel> */}
              <Select
                labelId="demo-select-small-label"
                id="demo-select-small"
                value={"Add"}
                label="Add"
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  return (
                    <Stack direction="row" gap="10px">
                      <Box>
                        <img
                          src="/images/static-icon/upload-icon.png"
                          style={{ width: "15px" }}
                        />
                      </Box>
                      <Typography variant={"h4"} sx={""}>
                        Add
                      </Typography>
                    </Stack>
                  );
                }}
              >
                <MenuItem sx={{ backgroundColor: "#fff" }} onClick={handleLogo}>
                  Logo
                </MenuItem>
                <MenuItem sx={{ backgroundColor: "#fff" }} onClick={handleColor}>
                  Color
                </MenuItem>
                <MenuItem sx={{ backgroundColor: "#fff" }} onClick={handleFont}>
                  Font
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          
          <AddLogo 
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            organization={organizationInfo}
            handleFileChange={handleFileChange}
            updateList={updateList}
          />
          <AddColor
            isOpen={isColorModalOpen}
            organization={organizationInfo}
            onClose={() => setIsColorModalOpen(false)}
            handleFileChange={handleFileChange}
            updateList={updateList}
          />
          <AddFont
            isOpen={isFontModalOpen}
            organization={organizationInfo}
            onClose={() => setIsFontModalOpen(false)}
            handleFileChange={handleFileChange}
            updateList={updateList}
          />

          <Stack gap={12}>
            <TableContainer component={Paper} sx={{ mb: "20px" }}>
              <Box>
                <Typography className={"sg-table-heading"} variant={"h2"}>
                  Logo
                </Typography>
              </Box>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left">Name</StyledTableCell>
                    <StyledTableCell align="left">Type</StyledTableCell>
                    <StyledTableCell align="left">Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logoList.length === 0 ? (
                      <Typography variant="h5" sx={{ textAlign: "right", padding: "20px" }}>
                        No logos available 
                      </Typography>
                    )
                  :
                    <>
                      {logoList.map((logo, index) => (
                        <>  
                          <StyledTableRow
                            // key={index}
                            sx={{ backgroundColor: "rgba(0,0,0,.05)" }}
                          >
                            <StyledTableCell
                              component="th"
                              scope="row"
                              align="left"
                            >
                              <Stack direction={"row"} gap={2} sx={{ alignItems:"center"}}>
                                <Box>
                                  <img src={logo?.fileUrl} alt="Image from Box" style={{ width: "50px", height: "50px", objectFit: "contain" }} />
                                </Box>
                                <Box>
                                  <Typography variant="h5" sx={{textTransform: "capitalize" }}>
                                    {logo.name}
                                  </Typography>
                                </Box>
                              </Stack>
                            </StyledTableCell>
                            <StyledTableCell align="left" sx={{textTransform:'capitalize'}}>
                              <Typography variant="h5" sx={{textTransform: "capitalize" }}>
                                {logo.type}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <Stack direction={"row"} gap={2} >
                                <Tooltip
                                  title="Download"
                                  arrow
                                  onClick={() => handleDownload(logo.fileUrl, logo.name)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <DownloadIcon />
                                </Tooltip>
                                <Tooltip
                                  title="Delete"
                                  onClick={() => {
                                    handleDeleteType('logos', logo._id);
                                  }}
                                  arrow
                                >
                                  <DeleteOutlineIcon />
                                </Tooltip>
                                {/* <Typography> Add to project</Typography> */}
                              </Stack>
                            </StyledTableCell>
                          </StyledTableRow>
                        </>
                      ))}
                    </>
                  }
                </TableBody>
              </Table>
              <Box sx={{ padding: "13px" }}>
                <Box sx={{ marginBottom: "20px" }}>
                  <Typography>Notes: </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    className={"topic-text"}
                    value={logoNote}
                    onChange={(e) => setLogoNote(e.target.value)}
                  />
                </Box>
                <Box sx={{ textAlign: "end" }}>
                  <Button variant="contained" onClick={handleNoteSubmit}>Add Notes</Button>
                </Box>
              </Box>
            </TableContainer>

            {/* Color */}
            <TableContainer component={Paper} sx={{ mb: "20px" }}>
              <Box>
                <Typography className={"sg-table-heading"} variant={"h2"}>
                  Color
                </Typography>
              </Box>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left">Name</StyledTableCell>
                    <StyledTableCell align="left">Type</StyledTableCell>
                    <StyledTableCell align="left">Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {colorList.length === 0 ? (
                      <Typography variant="h5" sx={{ textAlign: "right", padding: "20px" }}>
                        No Colors available 
                      </Typography>
                    )
                  :
                    <>
                      {colorList.map((color, index) => (
                        <>  
                          <StyledTableRow
                            // key={index}
                            sx={{ backgroundColor: "rgba(0,0,0,.05)" }}
                          >
                            <StyledTableCell
                              component="th"
                              scope="row"
                              align="left"
                            >
                              <Stack direction={"row"} gap={2} sx={{ alignItems:"center"}}>
                                <Box>
                                  <div style={{ width: "50px", height: "50px", backgroundColor: `${color.hex}` }} />
                                </Box>
                                <Box>
                                  <Typography variant="h5" sx={{textTransform: "capitalize" }}>
                                    {color.name}
                                  </Typography>
                                </Box>
                              </Stack>
                            </StyledTableCell>
                            <StyledTableCell align="left" sx={{textTransform:'capitalize'}}>
                              <Typography variant="h5" sx={{textTransform: "capitalize" }}>
                                {color.type}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <Stack direction={"row"} gap={2} >
                                {/* <Tooltip
                                  title="Download"
                                  arrow
                                  onClick={() => handleDownload(color.fileUrl, logo.name)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <DownloadIcon />
                                </Tooltip> */}
                                <Tooltip
                                  title="Delete"
                                  onClick={() => {
                                    handleDeleteType('colors', color._id);
                                  }}
                                  arrow
                                >
                                  <DeleteOutlineIcon />
                                </Tooltip>
                                {/* <Typography> Add to project</Typography> */}
                              </Stack>
                            </StyledTableCell>
                          </StyledTableRow>
                        </>
                      ))}
                    </>
                  }
                </TableBody>
              </Table>
              <Box sx={{ padding: "13px" }}>
                <Box sx={{ marginBottom: "20px" }}>
                  <Typography>Notes: </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    className={"topic-text"}
                    value={colorNote}
                    onChange={(e) => setColorNote(e.target.value)}
                  />
                </Box>
                <Box sx={{ textAlign: "end" }}>
                  <Button variant="contained" onClick={handleNoteSubmit}>Add Notes</Button>
                </Box>
              </Box>
            </TableContainer>

            {/* COLOR */}
            <TableContainer component={Paper} sx={{ mb: "20px" }}>
              <Box>
                <Typography className={"sg-table-heading"} variant={"h2"}>
                  Font
                </Typography>
              </Box>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left">Name</StyledTableCell>
                    <StyledTableCell align="left">Type</StyledTableCell>
                    <StyledTableCell align="left">Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fontList.length === 0 ? (
                      <Typography variant="h5" sx={{ textAlign: "right", padding: "20px" }}>
                        No Fonts available 
                      </Typography>
                    )
                  :
                    <>
                      {fontList.map((font, index) => (
                        <>  
                          <StyledTableRow
                            // key={index}
                            sx={{ backgroundColor: "rgba(0,0,0,.05)" }}
                          >
                            <StyledTableCell
                              component="th"
                              scope="row"
                              align="left"
                            >
                              <Stack direction={"row"} gap={2} sx={{ alignItems:"center"}}>
                                <Box>
                                  <img src={require('../assets/assets-icon/font.png')} alt="Image from Box" style={{ width: "50px", height: "50px", objectFit: "contain" }} />
                                </Box>
                                <Box>
                                  <Typography variant="h5" sx={{textTransform: "capitalize" }}>
                                    {font.name}
                                  </Typography>
                                </Box>
                              </Stack>
                            </StyledTableCell>
                            <StyledTableCell align="left" sx={{textTransform:'capitalize'}}>
                              <Typography variant="h5" sx={{textTransform: "capitalize" }}>
                                {font.type}
                              </Typography>
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <Stack direction={"row"} gap={2} >
                                <Tooltip
                                  title="Download"
                                  arrow
                                  onClick={() => handleDownload(font.fileUrl, font.name)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <DownloadIcon />
                                </Tooltip>
                                <Tooltip
                                  title="Delete"
                                  onClick={() => {
                                    handleDeleteType('fonts', font._id);
                                  }}
                                  arrow
                                >
                                  <DeleteOutlineIcon />
                                </Tooltip>
                                {/* <Typography> Add to project</Typography> */}
                              </Stack>
                            </StyledTableCell>
                          </StyledTableRow>
                        </>
                      ))}
                    </>
                  }
                </TableBody>
              </Table>
              <Box sx={{ padding: "13px" }}>
                <Box sx={{ marginBottom: "20px" }}>
                  <Typography>Notes: </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    className={"topic-text"}
                    value={fontNote}
                    onChange={(e) => setFontNote(e.target.value)}
                  />
                </Box>
                <Box sx={{ textAlign: "end" }}>
                  <Button variant="contained" onClick={handleNoteSubmit}>Add Notes</Button>
                </Box>
              </Box>
            </TableContainer>
          </Stack>
          
          <DeleteConfirmationModal
            open={open}
            onClose={() => setOpen(false)}
            onConfirm={handleDelete}
            isLoading={deleteLoading}
          />

        </Box>
      )
    }
    
    </>
  );
}

export default StyleGuide;
